import React from "react";
import type { ReactElement } from "react";

import type { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import type { StackNavigationProp } from "@react-navigation/stack";

import type { HubTabNavigatorProps } from "pianofunclub-crm/navigation/HubNavigator";
import defaultNavOptions from "pianofunclub-crm/navigation/utils/defaultNavigationOptions";
import type {
  SchoolParams,
  AccountParams,
  DefaultTermDatesParams,
  EnrolmentParams,
  InvoiceParams,
} from "pianofunclub-crm/navigation/utils/screenParams";
import AccountScreen, {
  screenOptions as accountScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountScreen";
import InvoiceScreen, {
  screenOptions as invoiceScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoiceScreen";
import DefaultTermDatesScreen, {
  screenOptions as defaultTermDatesScreenOptions,
} from "pianofunclub-crm/screens/schools/DefaultTermDatesScreen";
import SchoolScreen, {
  screenOptions as schoolScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolScreen";
import SchoolsHubScreen, {
  screenOptions as schoolsHubScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolsHubScreen";
import EnrolmentScreen, {
  screenOptions as enrolmentScreenOptions,
} from "pianofunclub-crm/screens/waitingList/EnrolmentScreen";

export type SchoolsStackNavigatorParamList = {
  Account: AccountParams;
  DefaultTermDates: DefaultTermDatesParams;
  Enrolment: EnrolmentParams;
  Invoice: InvoiceParams;
  School: SchoolParams;
  SchoolsHub?: {
    orderBy?: string;
    searchTerm?: string;
    status?: string;
  };
};

type NavigatorProps<T extends keyof SchoolsStackNavigatorParamList> = StackNavigationProp<
  SchoolsStackNavigatorParamList,
  T
>;

export type SchoolsStackNavigatorProps<T extends keyof SchoolsStackNavigatorParamList> =
  CompositeNavigationProp<NavigatorProps<T>, HubTabNavigatorProps<"SchoolsHubTab">>;

export type SchoolsStackRouteProps<T extends keyof SchoolsStackNavigatorParamList> = RouteProp<
  SchoolsStackNavigatorParamList,
  T
>;

const SchoolsStackNavigator = createStackNavigator<SchoolsStackNavigatorParamList>();

const SchoolsNavigator = (): ReactElement => {
  return (
    <SchoolsStackNavigator.Navigator screenOptions={defaultNavOptions}>
      <SchoolsStackNavigator.Screen
        component={SchoolsHubScreen}
        name="SchoolsHub"
        options={schoolsHubScreenOptions}
      />
      <SchoolsStackNavigator.Screen
        component={SchoolScreen}
        name="School"
        options={schoolScreenOptions}
      />
      <SchoolsStackNavigator.Screen
        component={AccountScreen}
        name="Account"
        options={accountScreenOptions}
      />
      <SchoolsStackNavigator.Screen
        component={InvoiceScreen}
        name="Invoice"
        options={invoiceScreenOptions}
      />
      <SchoolsStackNavigator.Screen
        component={DefaultTermDatesScreen}
        name="DefaultTermDates"
        options={defaultTermDatesScreenOptions}
      />
      <SchoolsStackNavigator.Screen
        component={EnrolmentScreen}
        name="Enrolment"
        options={enrolmentScreenOptions}
      />
    </SchoolsStackNavigator.Navigator>
  );
};

export default SchoolsNavigator;
